/**
 * Polar Manager Module
 * Handles loading, parsing, and managing glider polar data
 * Uses Condor binary .pol files with cubic Bezier interpolation
 */

import {
  parseBinaryPolarFile,
  parsePlaneInfoFile,
  parsePlanePdtFile,
  parseCotasaVneData,
  getCotasaVneForVariant
} from './condorPolarParser.js';

import {
  getAdjustedPolarProfile,
  interpolateSinkAtSpeed,
  getPolarDefaultWingLoading,
  clampPolarWingLoading
} from './polarInterpolator.js';

/**
 * Polar Manager Class
 * Manages polar data for different glider types using Condor binary .pol files
 */
export class PolarManager {
  constructor() {
    this.polarData = new Map();        // planeName -> polar object (dense points)
    this.polarCoeffCache = new Map();  // planeName -> [a, b, c] base coefficients
    this.profileCache = new Map();     // `${planeName}:${wingLoading}` -> profile
    this.failedPolars = new Set();
    this._cotasaVnePromise = null;     // Cached VNE data promise
    this._cotasaVneMap = null;         // Resolved VNE map
    this._weGlidePolarNames = new Set(); // Track which polars came from WeGlide JSON
    this._weGlidePolarData = null;       // Raw WeGlide JSON entries (for handicap lookup etc.)
    this._weGlideLoadPromise = null;     // Cached load promise
  }

  _getSavedPolarMappings() {
    if (typeof localStorage === 'undefined') return {};
    try {
      const parsed = JSON.parse(localStorage.getItem('igc_polar_mappings') || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  resolvePolarName(planeName) {
    if (!planeName) return planeName;
    const mappings = this._getSavedPolarMappings();
    return mappings[planeName] || planeName;
  }

  /**
   * Load polar data from a polar object (bulk load)
   * @param {object} polars - Object mapping plane names to polar data
   */
  loadPolarData(polars) {
    Object.entries(polars).forEach(([planeName, polarData]) => {
      if (this.validatePolarData(polarData)) {
        this.polarData.set(planeName, polarData);
      } else {
        console.warn(`Invalid polar data for ${planeName}`);
      }
    });
  }

  /**
   * Validate polar data structure (binary format with dense points)
   * @param {object} polar - Polar data object
   * @returns {boolean} True if valid
   */
  validatePolarData(polar) {
    if (!polar) return false;
    if (!Array.isArray(polar.polarSpeeds) || polar.polarSpeeds.length < 3) return false;
    if (!Array.isArray(polar.polarSinks) || polar.polarSinks.length < 3) return false;
    if (polar.polarSpeeds.length !== polar.polarSinks.length) return false;
    return true;
  }

  /**
   * Get polar data for a specific plane
   * @param {string} planeName - Name of the plane
   * @returns {object|null} Polar data or null if not found
   */
  getPolarData(planeName) {
    const resolvedPlaneName = this.resolvePolarName(planeName);
    return this.polarData.get(resolvedPlaneName) || this.polarData.get(planeName) || null;
  }

  /**
   * Get or build a wing-loading-adjusted profile (cached)
   * @param {string} planeName
   * @param {number|null} wingLoading
   * @returns {object|null} profile with .points[], .minSpeedMs, .maxSpeedMs
   */
  _getProfile(planeName, wingLoading = null) {
    const resolvedPlaneName = this.resolvePolarName(planeName);
    const polar = this.polarData.get(resolvedPlaneName) || this.polarData.get(planeName);
    if (!polar) return null;

    const resolvedLoading = clampPolarWingLoading(polar, wingLoading);
    const wl = resolvedLoading !== null ? Math.round(resolvedLoading * 100) / 100 : 'ref';
    const cacheKey = `${resolvedPlaneName}:${wl}`;
    let profile = this.profileCache.get(cacheKey);
    if (!profile) {
      profile = getAdjustedPolarProfile(polar, resolvedLoading);
      if (profile) {
        this.profileCache.set(cacheKey, profile);
      }
    }
    return profile;
  }

  /**
   * Get quadratic polar coefficients [a, b, c] where sink = a*v² + b*v + c
   * Coefficients are for speed in m/s, sink in m/s
   * Fitted from dense Bezier-interpolated polar curve (~100+ points)
   *
   * @param {string} planeName - Name of the plane
   * @param {number} currentLoading - Current wing loading in kg/m² (optional)
   * @returns {Array|null} [a, b, c] coefficients or null if not available
   */
  getPolarCoefficients(planeName, currentLoading = null) {
    const resolvedPlaneName = this.resolvePolarName(planeName);
    const polar = this.polarData.get(resolvedPlaneName) || this.polarData.get(planeName);
    const resolvedLoading = polar ? clampPolarWingLoading(polar, currentLoading) : currentLoading;

    // If no polar data at all, check if we have externally registered coefficients
    if (!polar) {
      const externalCoeffs = this.polarCoeffCache.get(resolvedPlaneName) || this.polarCoeffCache.get(planeName);
      if (!externalCoeffs) return null;
      // External coefficients — no wing loading adjustment possible without full polar
      return [...externalCoeffs];
    }

    // External polar entries (from WeGlide API etc.) have _external flag and no dense curve
    if (polar._external) {
      const baseCoeffs = this.polarCoeffCache.get(resolvedPlaneName) || this.polarCoeffCache.get(planeName);
      if (!baseCoeffs) return null;
      if (resolvedLoading === null) return [...baseCoeffs];

      // Apply wing loading scaling if we have reference data
      const refLoading = polar.defaultWingLoading;
      if (!Number.isFinite(refLoading) || refLoading <= 0) return [...baseCoeffs];
      if (Math.abs(resolvedLoading - refLoading) < 1e-6) return [...baseCoeffs];

      const scaleFactor = Math.sqrt(resolvedLoading / refLoading);
      return [
        baseCoeffs[0] / scaleFactor,
        baseCoeffs[1],
        baseCoeffs[2] * scaleFactor
      ];
    }

    // Standard path: get the wing-loading-adjusted profile from dense polar curve
    const profile = this._getProfile(planeName, resolvedLoading);
    if (!profile || profile.points.length < 3) return null;

    // Check if we have cached base coefficients (for the reference loading)
    // and can apply the wing loading adjustment analytically
    let baseCoeffs = this.polarCoeffCache.get(resolvedPlaneName);
    if (!baseCoeffs) {
      // Fit quadratic to the dense profile at reference loading
      const refProfile = this._getProfile(resolvedPlaneName, null);
      if (!refProfile || refProfile.points.length < 3) return null;

      const x = refProfile.points.map(p => p.speedMs);
      const y = refProfile.points.map(p => p.sinkMs);
      baseCoeffs = PolarManager.polyfit(x, y);
      if (!baseCoeffs) return null;

      this.polarCoeffCache.set(resolvedPlaneName, baseCoeffs);
    }

    // If no wing loading adjustment needed, return base coefficients
    if (resolvedLoading === null) return [...baseCoeffs];

    // Get reference wing loading
    const refLoading = Number.isFinite(polar.referenceWingLoading) && polar.referenceWingLoading > 0
      ? polar.referenceWingLoading
      : getPolarDefaultWingLoading(polar);

    if (!Number.isFinite(refLoading) || refLoading <= 0) return [...baseCoeffs];
    if (Math.abs(resolvedLoading - refLoading) < 1e-6) return [...baseCoeffs];

    const scaleFactor = Math.sqrt(resolvedLoading / refLoading);
    return [
      baseCoeffs[0] / scaleFactor,
      baseCoeffs[1],
      baseCoeffs[2] * scaleFactor
    ];
  }

  /**
   * Get sink rate for a given speed using direct interpolation on the dense polar curve
   *
   * @param {string} planeName - Name of the plane
   * @param {number} speed_kmh - Speed in km/h
   * @param {number} currentLoading - Current wing loading in kg/m² (optional)
   * @returns {number|null} Sink rate in m/s or null if not available
   */
  getPolarSink(planeName, speed_kmh, currentLoading = null) {
    const profile = this._getProfile(planeName, currentLoading);
    if (!profile) return null;

    const speed_mps = speed_kmh / 3.6;
    return interpolateSinkAtSpeed(profile, speed_mps);
  }

  /**
   * Get polar points for plotting or analysis
   * Samples directly from the dense Bezier-interpolated curve
   *
   * @param {string} planeName - Name of the plane
   * @param {number} currentLoading - Current wing loading in kg/m²
   * @param {number} numPoints - Number of points to generate (default: 50)
   * @returns {object} {speeds_kmh: [], sinks_mps: []} or null
   */
  getPolarPoints(planeName, currentLoading, numPoints = 50) {
    const profile = this._getProfile(planeName, currentLoading);
    if (!profile || profile.points.length < 2) return null;

    const minSpeedKmh = profile.minSpeedMs * 3.6;
    const maxSpeedKmh = profile.maxSpeedMs * 3.6;

    const speeds_kmh = [];
    const sinks_mps = [];

    for (let i = 0; i < numPoints; i++) {
      const speed_kmh = minSpeedKmh + (maxSpeedKmh - minSpeedKmh) * i / (numPoints - 1);
      const speed_mps = speed_kmh / 3.6;
      const sink = interpolateSinkAtSpeed(profile, speed_mps);
      if (sink !== null) {
        speeds_kmh.push(speed_kmh);
        sinks_mps.push(sink);
      }
    }

    return { speeds_kmh, sinks_mps };
  }

  /**
   * Quadratic least squares fit: y = a*x² + b*x + c
   * @param {Array<number>} x
   * @param {Array<number>} y
   * @returns {Array|null} [a, b, c] or null
   */
  static polyfit(x, y) {
    if (x.length !== y.length || x.length < 3) return null;

    const n = x.length;
    let sum_x = 0, sum_x2 = 0, sum_x3 = 0, sum_x4 = 0;
    let sum_y = 0, sum_xy = 0, sum_x2y = 0;

    for (let i = 0; i < n; i++) {
      const xi = x[i];
      const yi = y[i];
      const xi2 = xi * xi;
      sum_x += xi;
      sum_x2 += xi2;
      sum_x3 += xi2 * xi;
      sum_x4 += xi2 * xi2;
      sum_y += yi;
      sum_xy += xi * yi;
      sum_x2y += xi2 * yi;
    }

    const det = sum_x4 * (sum_x2 * n - sum_x * sum_x) -
                sum_x3 * (sum_x3 * n - sum_x * sum_x2) +
                sum_x2 * (sum_x3 * sum_x - sum_x2 * sum_x2);

    if (Math.abs(det) < 1e-10) return null;

    const a = (sum_x2y * (sum_x2 * n - sum_x * sum_x) -
               sum_xy * (sum_x3 * n - sum_x * sum_x2) +
               sum_y * (sum_x3 * sum_x - sum_x2 * sum_x2)) / det;

    const b = (sum_x4 * (sum_xy * n - sum_y * sum_x) -
               sum_x3 * (sum_x2y * n - sum_y * sum_x2) +
               sum_x2 * (sum_x2y * sum_x - sum_xy * sum_x2)) / det;

    const c = (sum_x4 * (sum_x2 * sum_y - sum_x * sum_xy) -
               sum_x3 * (sum_x3 * sum_y - sum_x * sum_x2y) +
               sum_x2 * (sum_x3 * sum_xy - sum_x2 * sum_x2y)) / det;

    return [a, b, c];
  }

  /**
   * Get all loaded polar names
   * @returns {Array} Array of plane names
   */
  getLoadedPolars() {
    return Array.from(this.polarData.keys());
  }

  /**
   * Check if polar data exists for a plane
   * @param {string} planeName - Name of the plane
   * @returns {boolean} True if polar data exists
   */
  hasPolar(planeName) {
    const resolvedPlaneName = this.resolvePolarName(planeName);
    return this.polarData.has(resolvedPlaneName) || this.polarData.has(planeName);
  }

  /**
   * Load CoTASA VNE data (cached)
   * @returns {Promise<Map>} Map of gliderName -> vneKmh
   */
  async _loadCotasaVne() {
    if (this._cotasaVneMap) return this._cotasaVneMap;
    if (this._cotasaVnePromise) return this._cotasaVnePromise;

    this._cotasaVnePromise = (async () => {
      try {
        const resp = await fetch('/polars/CoTASA_data.txt');
        if (!resp.ok) return new Map();
        const content = await resp.text();
        this._cotasaVneMap = parseCotasaVneData(content);
        return this._cotasaVneMap;
      } catch (_) {
        this._cotasaVneMap = new Map();
        return this._cotasaVneMap;
      }
    })();

    return this._cotasaVnePromise;
  }

  /**
   * Load binary .pol file on-demand with metadata from .pdt and plane-info
   *
   * @param {string} planeName - Name of the plane (e.g., "ASG29-18")
   * @returns {Promise<boolean>} True if successfully loaded
   */
  async loadPolarOnDemand(planeName) {
    const resolvedPlaneName = this.resolvePolarName(planeName);
    if (this.hasPolar(planeName)) return true;
    if (this.failedPolars.has(resolvedPlaneName)) return false;

    try {
      // Strip -FXB suffix for metadata lookup (base glider has the same metadata)
      const baseName = resolvedPlaneName.replace(/-FXB$/, '');

      // Fetch binary .pol, .pdt metadata, and plane-info in parallel
      const [polResponse, pdtResponse, infoResponse, cotasaVne] = await Promise.all([
        fetch(`/polars/${resolvedPlaneName}.pol`),
        fetch(`/polars/pdt/${baseName}.pdt`).catch(() => null),
        fetch(`/polars/plane-info/${baseName}.txt`).catch(() => null),
        this._loadCotasaVne()
      ]);

      if (!polResponse.ok) {
        console.warn(`Polar file not found: ${resolvedPlaneName}.pol`);
        this.failedPolars.add(resolvedPlaneName);
        return false;
      }

      const polBuffer = await polResponse.arrayBuffer();

      // Parse metadata (may fail, that's OK)
      let planeInfo = null;
      let planePdt = null;

      if (pdtResponse && pdtResponse.ok) {
        const pdtBuffer = await pdtResponse.arrayBuffer();
        planePdt = parsePlanePdtFile(pdtBuffer);
      }

      if (infoResponse && infoResponse.ok) {
        const infoContent = await infoResponse.text();
        planeInfo = parsePlaneInfoFile(infoContent);
      }

      // Parse binary polar with metadata
      const polarData = parseBinaryPolarFile(polBuffer, planeInfo, planePdt);

      if (polarData && this.validatePolarData(polarData)) {
        // Attach VNE from CoTASA data
        const vne = getCotasaVneForVariant(cotasaVne, resolvedPlaneName);
        if (vne) polarData.vneKmh = vne;

        this.polarData.set(resolvedPlaneName, polarData);
        return true;
      } else {
        console.warn(`Invalid binary polar data in ${resolvedPlaneName}.pol`);
        this.failedPolars.add(resolvedPlaneName);
        return false;
      }
    } catch (error) {
      console.error(`Error loading polar ${resolvedPlaneName}: ${error.message}`);
      this.failedPolars.add(resolvedPlaneName);
      return false;
    }
  }

  /**
   * Get polar data, loading on-demand if needed
   * @param {string} planeName - Name of the plane
   * @returns {Promise<object|null>} Polar data or null if not found
   */
  async getPolarDataAsync(planeName) {
    if (this.hasPolar(planeName)) return this.getPolarData(planeName);
    const loaded = await this.loadPolarOnDemand(planeName);
    return loaded ? this.getPolarData(planeName) : null;
  }

  /**
   * Get polar coefficients, loading on-demand if needed
   * @param {string} planeName - Name of the plane
   * @param {number} currentLoading - Current wing loading in kg/m² (optional)
   * @returns {Promise<Array|null>} [a, b, c] coefficients or null
   */
  async getPolarCoefficientsAsync(planeName, currentLoading = null) {
    await this.loadPolarOnDemand(planeName);
    return this.getPolarCoefficients(planeName, currentLoading);
  }

  /**
   * Register externally-provided polar coefficients (e.g. from WeGlide API).
   * Used as fallback when no Condor binary .pol file exists for a glider.
   * Does NOT overwrite existing loaded polars or previously loaded binary data.
   *
   * @param {string} planeName - Glider name (e.g. "ASW 19")
   * @param {Array<number>} coeffs - [a, b, c] quadratic coefficients (speed m/s, sink m/s)
   * @param {number|null} referenceMass - Reference mass in kg (for wing loading calc)
   * @param {number|null} wingArea - Wing area in m² (for wing loading calc)
   */
  registerExternalCoefficients(planeName, coeffs, referenceMass = null, wingArea = null, silent = false) {
    if (!planeName || !coeffs || coeffs.length < 3) return;
    // Don't overwrite Condor polars or already-registered external polars
    if (this.hasPolar(planeName)) return;
    if (this.polarCoeffCache.has(planeName)) return;

    const [a, b, c] = coeffs;
    if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(c)) return;

    this.polarCoeffCache.set(planeName, [a, b, c]);

    // Store minimal metadata for wing loading calculations
    if (Number.isFinite(referenceMass) && referenceMass > 0 &&
        Number.isFinite(wingArea) && wingArea > 0) {
      const refWingLoading = referenceMass / wingArea;
      this.polarData.set(planeName, {
        _external: true,
        defaultWingLoading: refWingLoading,
        referenceWeight: referenceMass,
        wingArea: wingArea
      });
    }

    if (!silent) {
      console.log(`[PolarManager] Registered external coefficients for "${planeName}": [${a.toFixed(6)}, ${b.toFixed(6)}, ${c.toFixed(6)}]`);
    }
  }

  /**
   * Load all WeGlide aircraft polars from the static JSON file.
   * Registers each as an external polar (won't overwrite Condor binary polars).
   * Safe to call multiple times — subsequent calls return the cached promise.
   *
   * @returns {Promise<string[]>} Sorted array of WeGlide polar names
   */
  async loadWeGlidePolars() {
    if (this._weGlideLoadPromise) return this._weGlideLoadPromise;

    this._weGlideLoadPromise = (async () => {
      try {
        const resp = await fetch('/polars/weglide-polars.json');
        if (!resp.ok) {
          console.warn('[PolarManager] Failed to fetch weglide-polars.json:', resp.status);
          return [];
        }
        const data = await resp.json();
        this._weGlidePolarData = data;

        let count = 0;
        for (const [name, entry] of Object.entries(data)) {
          if (!entry.coeffs || entry.coeffs.length < 3) continue;
          this._weGlidePolarNames.add(name);
          this.registerExternalCoefficients(
            name,
            entry.coeffs,
            entry.referenceMass || null,
            entry.wingArea || null,
            true // silent — bulk load
          );
          count++;
        }

        console.log(`[PolarManager] Loaded ${count} WeGlide polars`);
        return Array.from(this._weGlidePolarNames).sort();
      } catch (err) {
        console.warn('[PolarManager] Error loading WeGlide polars:', err.message);
        return [];
      }
    })();

    return this._weGlideLoadPromise;
  }

  /**
   * Get sorted array of WeGlide polar names (only available after loadWeGlidePolars).
   * @returns {string[]}
   */
  getWeGlidePolarNames() {
    return Array.from(this._weGlidePolarNames).sort();
  }

  /**
   * Check if a polar name is a WeGlide polar (vs Condor binary).
   * @param {string} planeName
   * @returns {boolean}
   */
  isWeGlidePolar(planeName) {
    return this._weGlidePolarNames.has(planeName);
  }

  /**
   * Get WeGlide polar metadata (handicap, manufacturer, etc.) for a given name.
   * @param {string} planeName
   * @returns {object|null}
   */
  getWeGlidePolarInfo(planeName) {
    return this._weGlidePolarData?.[planeName] || null;
  }
}
